/**
 * ============================================================
 * useAuditLog.ts — Hook de Registro de Auditoria
 * ============================================================
 *
 * Responsabilidade: Fornecer funcao `log()` para registrar acoes
 *   do usuario na tabela audit_logs do Supabase, criando trilha
 *   de auditoria para compliance e seguranca.
 *
 * Retorno: { log } — funcao async para registrar uma acao.
 *
 * Efeitos colaterais: Insere registro na tabela audit_logs
 *   com usuario, acao, entidade e detalhes adicionais.
 *
 * Quando usar: Apos acoes significativas como exclusao, mudanca
 *   de role, exportacao de dados, etc.
 *
 * @example
 *   const { log } = useAuditLog();
 *   await log('lead_deleted', 'lead', leadId, { reason: 'duplicado' });
 * ============================================================
 */

import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { useStore } from '@/store/useStore';
import { useCallback } from 'react';

/** Acoes rastreadas pelo sistema de auditoria */
type AuditAction =
  | 'login' | 'logout'
  | 'role_change' | 'member_added' | 'member_removed'
  | 'lead_deleted' | 'task_deleted' | 'client_deleted' | 'epic_deleted'
  | 'lead_created' | 'task_created' | 'client_created'
  | 'lead_converted'
  | 'bulk_tag_add' | 'bulk_tag_remove'
  | 'lead_moved' | 'lead_updated' | 'task_updated' | 'task_status_changed'
  | 'settings_changed' | 'api_token_regenerated'
  | 'filter_saved' | 'filter_deleted'
  | 'integration_connected' | 'integration_disconnected'
  | 'bulk_delete' | 'data_exported' | 'data_imported'
  | 'webhook_created' | 'webhook_deleted'
  | 'column_added' | 'column_deleted' | 'column_reordered'
  | 'epic_created' | 'epic_updated'
  | 'wiki_page_created' | 'wiki_page_updated' | 'wiki_page_deleted'
  | 'automation_created' | 'automation_updated' | 'automation_deleted';

export function useAuditLog() {
  const activeProjectId = useStore((s) => s.activeProjectId);
  const activeWorkspaceId = useStore((s) => s.activeWorkspaceId);

  const log = useCallback(async (
    action: AuditAction,
    entityType: string,
    entityId?: string,
    details?: Record<string, unknown>
  ) => {
    if (!activeProjectId) return;
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data: profile } = await supabase
      .from('profiles')
      .select('name')
      .eq('id', user.id)
      .maybeSingle();

    await supabase.from('audit_logs').insert({
      project_id: activeProjectId,
      // Membros → atividade agrega por workspace; sem isso a coluna ficava NULL.
      workspace_id: activeWorkspaceId || null,
      user_id: user.id,
      user_name: profile?.name || user.email || 'Unknown',
      action,
      entity_type: entityType,
      entity_id: entityId || null,
      details: details || {},
    } as Database['public']['Tables']['audit_logs']['Insert']);
  }, [activeProjectId, activeWorkspaceId]);

  return { log };
}
